"use client";

import { motion } from "framer-motion";
import { Truck, Ship, Plane, Bike, PackageCheck, MapPin, Clock, Route } from "lucide-react";

const fleet = [
  {
    icon: Truck,
    name: "Camions Frigorifiques",
    capacity: "5 à 20 tonnes",
    range: "National & sous-régional",
    desc: "Plateaux isothermes avec groupe froid pour les longues distances. Suivi GPS temps réel de chaque chargement.",
  },
  {
    icon: Bike,
    name: "Motos Livreuses",
    capacity: "50 à 200 kg",
    range: "Last-mile urbain",
    desc: "Pour les livraisons rapides en ville : marchés, restaurants, particuliers. Réseau agile et réactif.",
  },
  {
    icon: Ship,
    name: "Conteneurs Maritimes",
    capacity: "20 à 40 pieds",
    range: "Export Oumma & Europe",
    desc: "Conteneurs réfrigérés (reefers) pour l'export vers le Nigeria, l'Europe et le monde. Documentation douanière complète.",
  },
  {
    icon: Plane,
    name: "Fret Aérien Express",
    capacity: "100 kg à 5 tonnes",
    range: "Express international",
    desc: "Pour les commandes urgentes et les produits frais premium à haute valeur ajoutée. Livraison 48h vers l'Europe.",
  },
];

const steps = [
  { icon: PackageCheck, title: "Préparation", text: "Commande préparée, pesée, étiquetée, scellée." },
  { icon: Route, title: "Acheminement", text: "Itinéraire optimisé, suivi GPS, géofencing." },
  { icon: Truck, title: "Transport", text: "Flotte adaptée au volume et à la fraîcheur." },
  { icon: MapPin, title: "Livraison", text: "Confirmation client, preuve de livraison numérique." },
];

export function Logistique() {
  return (
    <section id="logistique" className="relative py-24 lg:py-32 overflow-hidden">
      {/* Background image with overlay */}
      <div className="absolute inset-0 -z-10">
        <img
          src="/images/logistique-flotte.png"
          alt="Flotte logistique PATATE OR"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background/95 via-background/90 to-background/95" />
      </div>

      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="divider-gold max-w-xs mx-auto mb-5"
          >
            <span className="text-xs uppercase tracking-[0.32em] text-gold-deep font-semibold">
              Logistique &amp; Transport
            </span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.05 }}
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight"
          >
            Une <span className="text-gradient-gold">flotte</span> au service de la
            fraîcheur
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mt-6 text-base sm:text-lg text-foreground/75 leading-relaxed"
          >
            Du quartier au continent, PATATE OR maîtrise toute la chaîne logistique.
            Camions frigorifiques, motos urbaines, conteneurs maritimes, fret aérien :
            nous adaptons le mode de transport à vos délais et à vos volumes.
          </motion.p>
        </div>

        {/* Fleet grid */}
        <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {fleet.map((f, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              className="group relative p-6 rounded-2xl bg-card/90 backdrop-blur border border-border hover:border-gold hover:shadow-gold transition-all duration-500 hover:-translate-y-1"
            >
              <div className="mb-4 grid place-items-center w-14 h-14 rounded-xl bg-gradient-gold shadow-gold group-hover:scale-110 transition-transform">
                <f.icon className="w-7 h-7 text-white" strokeWidth={2} />
              </div>

              <h3 className="font-display text-lg font-bold text-foreground">
                {f.name}
              </h3>

              <div className="mt-2 flex flex-wrap gap-2">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-secondary text-[10px] font-semibold uppercase tracking-wider text-gold-deep">
                  <PackageCheck className="w-3 h-3" />
                  {f.capacity}
                </span>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-secondary text-[10px] font-semibold uppercase tracking-wider text-gold-deep">
                  <MapPin className="w-3 h-3" />
                  {f.range}
                </span>
              </div>

              <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
                {f.desc}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Process strip */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mt-14 p-6 sm:p-10 rounded-3xl bg-gradient-earth text-cream"
        >
          <div className="text-center mb-8">
            <h3 className="font-display text-2xl sm:text-3xl font-bold text-cream">
              Notre processus logistique
            </h3>
            <p className="mt-2 text-cream/70 text-sm">
              Quatre étapes, zéro improvisation. Chaque livraison est tracée et confirmée.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((s, i) => (
              <div key={i} className="relative">
                <div className="flex items-center gap-3 mb-3">
                  <div className="grid place-items-center w-10 h-10 rounded-full bg-gradient-gold text-white font-bold text-sm shadow-gold">
                    {i + 1}
                  </div>
                  <s.icon className="w-6 h-6 text-gold" />
                </div>
                <h4 className="font-display text-lg font-bold text-cream">
                  {s.title}
                </h4>
                <p className="mt-1 text-sm text-cream/75 leading-relaxed">
                  {s.text}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-8 flex items-center justify-center gap-3 text-cream/80 text-sm">
            <Clock className="w-4 h-4 text-gold" />
            <span>Délai moyen de livraison national : 24–72h · International : 5–15 jours</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
